import { useState, useEffect } from "react";
import { api, formatApiError } from "../lib/api";
import { useData } from "../contexts/DataContext";
import { toast } from "sonner";

function buildTaskPayload({ task, parentId, selProject, title, description, status, priority, assignees, startDate, dueDate, dueTime }) {
  const base = {
    title: title.trim(), description, status, priority, assignees,
    start_date: startDate || null, due_date: dueDate || null, due_time: dueTime || null,
    tz_offset: -new Date().getTimezoneOffset(),
  };
  return task ? base : { ...base, project_id: selProject, parent_id: parentId };
}

async function saveTask(task, payload, upsertTask) {
  const { data } = task
    ? await api.patch(`/tasks/${task.id}`, payload)
    : await api.post("/tasks", payload);
  upsertTask(data);
  return data;
}

function successMessage(task, parentId) {
  if (task) return "Task updated";
  return parentId ? "Subtask added" : "Task created";
}

export function useTaskDialogForm({ open, task, parentId, defaultStatus, defaultDueDate, projectId, onClose }) {
  const { upsertTask, refreshTasks } = useData();
  const [selProject, setSelProject] = useState(projectId);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [status, setStatus] = useState(defaultStatus);
  const [priority, setPriority] = useState("normal");
  const [assignees, setAssignees] = useState([]);
  const [startDate, setStartDate] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [dueTime, setDueTime] = useState("");
  const [saving, setSaving] = useState(false);

  // Form state is intentionally re-initialized only when the dialog opens or the task changes.
  useEffect(() => {
    if (!open) return;
    setSelProject(projectId);
    setTitle(task?.title || "");
    setDescription(task?.description || "");
    setStatus(task?.status || defaultStatus);
    setPriority(task?.priority || "normal");
    setAssignees(task?.assignees || []);
    setStartDate(task?.start_date || "");
    setDueDate(task?.due_date || defaultDueDate || "");
    setDueTime(task?.due_time || "");
  }, [open, task, defaultStatus, defaultDueDate, projectId]);

  const toggleAssignee = (id) =>
    setAssignees((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    setSaving(true);
    try {
      await saveTask(task, buildTaskPayload({ task, parentId, selProject, title, description, status, priority, assignees, startDate, dueDate, dueTime }), upsertTask);
      toast.success(successMessage(task, parentId));
      refreshTasks();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return {
    selProject, setSelProject, title, setTitle, description, setDescription,
    status, setStatus, priority, setPriority, assignees, toggleAssignee,
    startDate, setStartDate, dueDate, setDueDate, dueTime, setDueTime,
    saving, submit,
  };
}
